/**
 * Menu.tsx — the ONE accessible dropdown-menu primitive.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: users-menu (admin polish).
 *
 * WHY: the header ProfileMenu (ui/chrome.tsx) hand-rolled the whole dropdown
 * a11y dance — a trigger with `aria-haspopup="menu"`/`aria-expanded`, a
 * `role="menu"` panel of `role="menuitem"` rows, Escape + outside-click to
 * close, and focus returning to the trigger on close. The Users tab now needs
 * the SAME behaviour for its per-row ⋯ actions, so rather than hand-roll a
 * second dropdown (CTO reuse mandate) the pattern lives here once. ProfileMenu
 * is refactored onto it (a custom pill trigger); the row menu uses the default
 * trailing kebab. Every value is a `var(--gps-*)` token so light/dark just work.
 *
 * The panel is rendered via a render-prop that receives `{ close }` so an item
 * can run its action and return focus to the trigger in one call; `MenuItem` is
 * a thin convenience for a button row (links pass `menuItemStyle` directly).
 *
 * KEYBOARD (WAI-ARIA APG menu-button pattern): opening moves focus into the
 * menu (first item; ArrowUp on the trigger opens on the last). Within the open
 * menu the items are roving-focus targets (tabindex -1) — ArrowUp/Down move and
 * wrap, Home/End jump to the ends, disabled rows are skipped, Escape/Tab leave
 * (close, focus back on the trigger), and an outside click closes. The roving
 * set is read from the DOM (`[role="menuitem"]`), so it works for the mixed
 * button/link rows consumers render through the render-prop.
 */
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';

/** The shared look of one dropdown row (button or link) inside a Menu. */
export const menuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '44px',
  padding: '11px 12px',
  border: 0,
  borderRadius: 'var(--gps-r-sm)',
  background: 'none',
  color: 'var(--gps-text)',
  textAlign: 'left',
  textDecoration: 'none',
  font: 'inherit',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};

/** The default trailing kebab (⋯) trigger chrome — a 44px-touch icon button. */
const kebabTriggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  padding: 0,
  border: 0,
  borderRadius: 'var(--gps-r-pill)',
  background: 'none',
  color: 'var(--gps-text)',
  font: 'inherit',
  fontSize: '20px',
  lineHeight: 1,
  cursor: 'pointer',
};

/** Handed to the panel render-prop so an item can close the menu (focus returns to the trigger). */
export interface MenuApi {
  close: () => void;
}

/**
 * An accessible dropdown menu. `triggerContent`/`triggerStyle` default to the
 * trailing ⋯ kebab; pass a custom pair (e.g. the profile pill) to reuse the
 * exact same open/close + a11y behaviour under a different face. `triggerLabel`
 * gives an icon-only trigger its accessible name (omit it when the trigger has
 * visible text). `menuLabel` names the `role="menu"` panel.
 */
export function Menu({
  menuLabel,
  triggerLabel,
  triggerContent,
  triggerStyle,
  wrapperStyle,
  wrapperClassName,
  menuStyle,
  align = 'end',
  children,
}: {
  menuLabel: string;
  triggerLabel?: string;
  triggerContent?: ReactNode;
  triggerStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  /** Class on the positioning wrapper — a hook for responsive show/hide (e.g. the header's desktop profile menu, hidden when the mobile hamburger takes over). */
  wrapperClassName?: string;
  menuStyle?: CSSProperties;
  align?: 'start' | 'end';
  children: (api: MenuApi) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Which end the next open should land focus on (WAI-ARIA APG: ArrowUp on the
  // closed trigger opens with focus on the LAST item, ArrowDown/click on the first).
  const openToRef = useRef<'first' | 'last'>('first');
  const menuId = useId();

  // Closing from an item (or Escape) returns focus to the trigger so keyboard
  // users are never dropped at the top of the document.
  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  // The enabled `role="menuitem"` rows in DOM order — the roving-focus targets.
  // Disabled rows (a `disabled`/`aria-disabled` MenuItem) are skipped, matching
  // their inert semantics; separators and the identity block are not menuitems.
  const getItems = useCallback((): HTMLElement[] => {
    const root = menuRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
      (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true',
    );
  }, []);

  // On open, move focus INTO the menu (APG menu-button pattern) — the items are
  // roving-focus targets (tabindex -1; Tab leaves the menu, arrows move within).
  useEffect(() => {
    if (!open) return;
    const items = getItems();
    for (const el of items) el.tabIndex = -1;
    const target = openToRef.current === 'last' ? items[items.length - 1] : items[0];
    target?.focus();
    openToRef.current = 'first';
  }, [open, getItems]);

  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function handleOutsideClick(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [open]);

  // Arrow-key navigation WITHIN the open menu (WAI-ARIA APG roving focus):
  // Up/Down move (wrapping), Home/End jump to the ends, Tab leaves the menu
  // (closes, focus back on the trigger for a predictable landing). Escape +
  // outside-click stay on the document listener above.
  const onMenuKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const items = getItems();
      if (items.length === 0) return;
      const focusAt = (i: number) => {
        const n = items.length;
        items[((i % n) + n) % n].focus();
      };
      const current = items.indexOf(document.activeElement as HTMLElement);
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusAt(current + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          focusAt(current - 1);
          break;
        case 'Home':
          event.preventDefault();
          focusAt(0);
          break;
        case 'End':
          event.preventDefault();
          focusAt(items.length - 1);
          break;
        case 'Tab':
          event.preventDefault();
          close();
          break;
        default:
          break;
      }
    },
    [getItems, close],
  );

  // On the CLOSED trigger, Down/Up open the menu (APG) — focusing the first or
  // last item respectively (handled by the open effect via openToRef).
  const onTriggerKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openToRef.current = 'first';
      setOpen(true);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openToRef.current = 'last';
      setOpen(true);
    }
  }, []);

  return (
    <div ref={wrapperRef} className={wrapperClassName} style={{ position: 'relative', ...wrapperStyle }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={triggerLabel}
        style={triggerStyle ?? kebabTriggerStyle}
      >
        {triggerContent ?? <span aria-hidden="true">⋯</span>}
      </button>

      {open ? (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label={menuLabel}
          onKeyDown={onMenuKeyDown}
          style={{
            position: 'absolute',
            ...(align === 'end' ? { right: 0 } : { left: 0 }),
            top: 'calc(100% + 8px)',
            minWidth: '12rem',
            background: 'var(--gps-surface)',
            border: '1px solid var(--gps-border)',
            borderRadius: 'var(--gps-r-md)',
            boxShadow: 'var(--gps-shadow)',
            padding: '6px',
            zIndex: 30,
            ...menuStyle,
          }}
        >
          {children({ close })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One menu row (button). Runs `onSelect` on click; the caller closes the menu
 * inside it. A `disabled` row greys out (muted text, default cursor) but STAYS
 * in the accessibility tree with its name — pass `title` for the hover tooltip
 * (UX Wave 8 §3: the waffle's coming-soon entries).
 */
export function MenuItem({
  children,
  onSelect,
  tone = 'default',
  disabled,
  title,
}: {
  children: ReactNode;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={title}
      onClick={onSelect}
      style={{
        ...menuItemStyle,
        color: tone === 'danger' ? 'var(--gps-danger-text)' : 'var(--gps-text)',
        ...(disabled ? { color: 'var(--gps-muted)', opacity: 0.65, cursor: 'default' } : {}),
      }}
    >
      {children}
    </button>
  );
}
